/**
 * The store's Twitch app, as the SPA reads it (docs/live-breaks.md, "Twitch"). Each
 * environment brings its own Twitch application; without one, every Twitch feature (linking,
 * Priority, the channel's status, the Twitch buttons and PiP, sponsored streams) stays hidden.
 * `features.liveBreaks` gates those surfaces on its own, so a caller checks both.
 */
import {
  type StorefrontTwitch,
  TWITCH_LINK_ERRORS,
  TWITCH_LINK_MESSAGE_TYPE,
  TWITCH_LINK_PATH,
  TWITCH_LINK_RESULT_PARAM,
  type TwitchLinkError,
} from "@loot-os/api-contract";
import { useCallback, useEffect, useRef, useState } from "react";

import { track } from "./analytics.js";
import { qk } from "./queries.js";
import { queryClient } from "./queryClient.js";
import type { TenantConfig } from "./tenant.js";

const OFF: StorefrontTwitch = { enabled: false, clientId: null };

/**
 * Fail-closed read: a config cached by an older BFF, or in sessionStorage, has no `twitch`, and
 * that is a store without a Twitch app. Enabled only with a client id to link through.
 */
export function twitchOf(config: TenantConfig): StorefrontTwitch {
  const twitch = (config as Partial<TenantConfig>).twitch as Partial<StorefrontTwitch> | undefined;
  if (twitch?.enabled !== true || typeof twitch.clientId !== "string" || !twitch.clientId) {
    return OFF;
  }
  return { enabled: true, clientId: twitch.clientId };
}

/** How a Twitch link came out: linked, refused (and why), or the popup closed with no answer. */
export type TwitchLinkResult = { ok: true } | { ok: false; error: TwitchLinkError } | { ok: null };

/** The popup's size, centred over the page (boxed's). */
const POPUP = { width: 500, height: 700 };
/** How often a popup's closing is checked for, when it closed without answering. */
const POPUP_CLOSED_POLL_MS = 500;

/** The store's own link route, coming back to `returnTo` (a same-origin path) when no popup answers. */
export function twitchLinkUrl(returnTo: string): string {
  return `${TWITCH_LINK_PATH}?${new URLSearchParams({ returnTo })}`;
}

function parseLinkError(raw: unknown): TwitchLinkError {
  return typeof raw === "string" && (TWITCH_LINK_ERRORS as readonly string[]).includes(raw)
    ? (raw as TwitchLinkError)
    : "unknown";
}

/** What the callback page posted (`{ type, ok, error? }`), or null for any other message. */
export function parseTwitchLinkMessage(data: unknown): TwitchLinkResult | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as { type?: unknown; ok?: unknown; error?: unknown };
  if (message.type !== TWITCH_LINK_MESSAGE_TYPE || typeof message.ok !== "boolean") return null;
  return message.ok ? { ok: true } : { ok: false, error: parseLinkError(message.error) };
}

/**
 * The link's result a page came back with when no popup could open (`?twitchLink=ok` or an error),
 * from its query string; null when it carries none.
 */
export function readTwitchLinkResult(search: string): TwitchLinkResult | null {
  const value = new URLSearchParams(search).get(TWITCH_LINK_RESULT_PARAM);
  if (value === null) return null;
  return value === "ok" ? { ok: true } : { ok: false, error: parseLinkError(value) };
}

/**
 * What a new link changes: the shopper's own link, and every live-break estimate, whose Priority
 * it decides. Each is read again.
 */
export function refreshAfterTwitchLink(): void {
  void queryClient.invalidateQueries({ queryKey: qk.twitchMe });
  void queryClient.invalidateQueries({ queryKey: ["private", "liveBreaks", "estimate"] });
}

/**
 * Opens the store's Twitch link in a popup and resolves with how it came out: the callback page,
 * on this origin, posts the result to this window and closes. A popup the browser blocks sends
 * the whole tab through the link instead, back to `returnTo` with the result in its query (read
 * there with `readTwitchLinkResult`); that promise never settles, since the page is leaving.
 */
export function openTwitchLinkPopup(returnTo: string): Promise<TwitchLinkResult> {
  const url = twitchLinkUrl(returnTo);
  const left = Math.max(0, window.screenX + (window.outerWidth - POPUP.width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - POPUP.height) / 2);
  const popup = window.open(
    url,
    "loot-twitch-link",
    `popup=yes,width=${POPUP.width},height=${POPUP.height},left=${left},top=${top}`,
  );
  if (!popup) {
    window.location.assign(url);
    return new Promise(() => {});
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: TwitchLinkResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(closedPoll);
      resolve(result);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const result = parseTwitchLinkMessage(event.data);
      if (result) finish(result);
    };
    window.addEventListener("message", onMessage);
    const closedPoll = setInterval(() => {
      if (popup.closed) finish({ ok: null });
    }, POPUP_CLOSED_POLL_MS);
  });
}

/**
 * Connect (or Reconnect) Twitch from a page: `connect()` records `twitch_connect_started` (boxed's
 * `trackTwitchConnectStarted`; the server's `twitch_account_linked` marks the links that finish)
 * and opens the popup, and when it answers the shopper's link and their live-break estimates are
 * read again and `onResult` hears how it went. `pending` while the popup is open. The page itself
 * reads a `?twitchLink=` it came back with.
 */
export function useTwitchLink({
  returnTo,
  onResult,
}: {
  /** The page to come back to when no popup could open. */
  returnTo: () => string;
  onResult?: (result: TwitchLinkResult) => void;
}): { connect: () => void; pending: boolean } {
  const [pending, setPending] = useState(false);
  const mounted = useRef(true);
  const report = useRef(onResult);
  report.current = onResult;
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const connect = useCallback(() => {
    track("twitch_connect_started", {});
    setPending(true);
    void openTwitchLinkPopup(returnTo()).then((result) => {
      if (mounted.current) setPending(false);
      if (result.ok !== null) refreshAfterTwitchLink();
      report.current?.(result);
    });
  }, [returnTo]);
  return { connect, pending };
}

/**
 * Whether Twitch's embedded player can keep a Twitch sign-in on this browser: WebKit (Safari, and
 * every browser on iOS) blocks the player's third-party cookies, so there the PiP player stays
 * signed out whatever the shopper links, and offers Connect Twitch with a pointer to another
 * browser instead of Sign in to Twitch (boxed's `isSafariOrIOS`).
 */
export function twitchPlayerKeepsSignIn(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator,
): boolean {
  const ua = nav.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && nav.maxTouchPoints > 1);
  const safari = /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(ua);
  return !iOS && !safari;
}
